// Roles + permissions — single source of truth on the frontend.

export const ROLES = [
  { id: "director",                label: "Director",         blurb: "Full workspace access — billing, members, all deals." },
  { id: "manager",                 label: "Manager",          blurb: "Manages negotiators, oversees pipeline & branch." },
  { id: "senior_sales_negotiator", label: "Senior Negotiator", blurb: "Owns deals, mentors negotiators, runs viewings." },
  { id: "sales_negotiator",        label: "Negotiator",        blurb: "Manages own pipeline of deals & viewings." },
  { id: "sales_progressor",        label: "Sales Progressor",  blurb: "Drives sales-progression chain, exchange & completion." },
  { id: "admin",                   label: "Admin",            blurb: "Office admin — invites, settings, no deal ownership." },
];

export const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.id, r.label]));
export const ROLE_BLURB = Object.fromEntries(ROLES.map((r) => [r.id, r.blurb]));

export const TEAM_ADMIN_ROLES = new Set(["director", "admin"]);

export const canManageTeam = (role) => TEAM_ADMIN_ROLES.has(role);
export const isOrgOwner = (member) => Boolean(member?.is_owner);
export const roleLabel = (role) => ROLE_LABEL[role] || "Member";

// Deterministic palette so the same person always gets the same colour.
const PALETTE = [
  ["from-indigo-500 to-indigo-700",   "ring-indigo-200"],
  ["from-blue-500 to-blue-700",       "ring-blue-200"],
  ["from-rose-500 to-rose-700",       "ring-rose-200"],
  ["from-amber-500 to-amber-600",     "ring-amber-200"],
  ["from-emerald-500 to-emerald-700", "ring-emerald-200"],
  ["from-fuchsia-500 to-fuchsia-700", "ring-fuchsia-200"],
  ["from-cyan-500 to-cyan-700",       "ring-cyan-200"],
  ["from-indigo-500 to-indigo-700",   "ring-indigo-200"],
];

export const colorForKey = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

export const initialsOf = (nameOrEmail = "") => {
  const s = String(nameOrEmail).trim();
  if (!s) return "—";
  if (s.includes("@")) {
    const local = s.split("@")[0];
    return local.slice(0, 2).toUpperCase();
  }
  return s
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || s.slice(0, 2).toUpperCase();
};


/* ═══════════════════════════════════════════════════════════════════
   Canonical role-gating sets · single source of truth
   ----------------------------------------------------------------
   Mirrors `backend/roles.py` exactly. Inline comparisons like
   `role === "admin"` elsewhere are a smell — import a named set or
   the `useRoleGate` hook / `<RoleGate>` component instead.

   Adding a new role to a gate (e.g. allow "manager" to be a steward)
   becomes a one-line change here AND in `backend/roles.py`.
   ──────────────────────────────────────────────────────────────── */

// Workspace stewards — owners of a workspace, allowed to seed sample
// data, run demo control, reset their environment. Backend mirror:
// `WORKSPACE_STEWARD_ROLES` in `backend/roles.py`.
export const WORKSPACE_STEWARD_ROLES = Object.freeze(["admin", "director"]);

// Platform admins — highest privilege tier (cross-org operations).
// Backend mirror: `PLATFORM_ADMIN_ROLES` in `backend/roles.py`.
export const PLATFORM_ADMIN_ROLES = Object.freeze(["admin"]);

// Director-tier navigation — roles that should see the Director
// Centre / Mission Control / Automations nav cluster. Slightly wider
// than stewards because managers also need executive visibility.
export const DIRECTOR_NAV_ROLES = Object.freeze(["admin", "director", "manager"]);

// Named registry consumed by useRoleGate / <RoleGate role="…" />.
// Every named set should appear here.
export const ROLE_SETS = Object.freeze({
  steward:        WORKSPACE_STEWARD_ROLES,
  platform_admin: PLATFORM_ADMIN_ROLES,
  director_nav:   DIRECTOR_NAV_ROLES,
});

export const normaliseRole = (role) => String(role || "").trim().toLowerCase();

/** Resolve a named set OR a custom array of role strings. */
const resolveRoles = (input) => {
  if (Array.isArray(input)) return input.map(normaliseRole);
  return ROLE_SETS[input] || [];
};

/** Pure predicate — true if `role` is allowed by `gate`. */
export const roleAllowed = (role, gate) =>
  resolveRoles(gate).includes(normaliseRole(role));

export const isSteward = (role) => roleAllowed(role, "steward");
export const isPlatformAdmin = (role) => roleAllowed(role, "platform_admin");
